import React, { useEffect } from "react";
import { ClipboardList, FileText, ShoppingCart, Truck, ArrowRight, TrendingUp } from "lucide-react";
import { Card, Row, Col, Badge, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { WarehouseOption } from "./DashboardFilters";
import { fetchOrderFlow } from "slices/Dashboard/Order_Flow_Tracker/thunk";
import "./OrderFlowTracker.css";

interface OrderFlowTrackerProps {
  dateFrom?: Date;
  dateTo?: Date;
  warehouse?: WarehouseOption[];
}


const flowStagesConfig = [
  { 
    icon: ClipboardList, 
    label: "Enquiry", 
    colorClass: "order-flow-tracker-icon-container-purple",
    cardClass: "order-flow-tracker-card-purple",
    gradient: "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 50%, #e9d5ff 100%)",
    borderColor: "#9333ea",
    iconBg: "linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)"
  },
  { 
    icon: FileText, 
    label: "Quotation", 
    colorClass: "order-flow-tracker-icon-container-indigo",
    cardClass: "order-flow-tracker-card-indigo",
    gradient: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 50%, #c7d2fe 100%)",
    borderColor: "#4f46e5",
    iconBg: "linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)"
  },
  { 
    icon: ShoppingCart, 
    label: "Order", 
    colorClass: "order-flow-tracker-icon-container-green",
    cardClass: "order-flow-tracker-card-green",
    gradient: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)",
    borderColor: "#16a34a",
    iconBg: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
  },
  { 
    icon: Truck, 
    label: "Dispatch", 
    colorClass: "order-flow-tracker-icon-container-amber",
    cardClass: "order-flow-tracker-card-amber",
    gradient: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 50%, #fde68a 100%)",
    borderColor: "#d97706",
    iconBg: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)"
  },
];

export function OrderFlowTracker({ dateFrom, dateTo, warehouse = [] }: OrderFlowTrackerProps) {
  const dispatch = useDispatch<any>();
  const { data: orderFlowData, loading } = useSelector((state: any) => state.OrderFlowTrackerReducer);

  useEffect(() => {
    dispatch(fetchOrderFlow({ dateFrom, dateTo, warehouse })).catch((error: any) => {
      console.error("Error fetching order flow data:", error);
      toast.error("Failed to load order flow data");
    });
  }, [dispatch, dateFrom, dateTo, warehouse]);

  // Build flow stages with actual data
  const flowStages = flowStagesConfig.map((config, index) => {
    const Icon = config.icon;
    let value = 0;
    let conversion: number | undefined = undefined;

    if (orderFlowData) {
      switch (index) {
        case 0: // Enquiry
          value = orderFlowData.enquiry?.count || 0;
          break;
        case 1: // Quotation
          value = orderFlowData.quotation?.count || 0;
          conversion = orderFlowData.quotation?.conversion;
          break;
        case 2: // Order
          value = orderFlowData.order?.count || 0;
          conversion = orderFlowData.order?.conversion;
          break;
        case 3: // Dispatch
          value = orderFlowData.dispatch?.count || 0;
          conversion = orderFlowData.dispatch?.conversion;
          break;
      }
    }

    return {
      ...config,
      Icon,
      value,
      conversion,
    };
  });

  return (
    <Card className="order-flow-tracker-card shadow-sm border-0">
      <Card.Body className="p-4">
        {/* Header */}
        <div className="mb-3 pb-2 border-bottom position-relative">
          <div className="order-summary-title-container">
            <ClipboardList className="order-summary-icon" />
            <div>
              <h3 className="order-summary-title">Order Pipeline Overview</h3>
              {orderFlowData && (
                <p className="mb-0 text-muted small" style={{ fontSize: '0.75rem' }}>Track your sales funnel performance</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Desktop Layout - Flexbox for 4 cards in one row */}
        <div className="d-none d-lg-flex align-items-stretch mb-0" style={{ gap: '0.75rem' }}>
          {flowStages.map((stage, index) => {
            const Icon = stage.Icon;
            const isLast = index === flowStages.length - 1;
            const showConversion = stage.conversion !== undefined && stage.conversion > 0;
            
            return (
              <React.Fragment key={stage.label}>
                <div className="d-flex" style={{ flex: '1 1 0', minWidth: 0 }}>
                  <Card 
                    className={`order-flow-tracker-stage-card flex-fill ${stage.cardClass} border-0 shadow-sm position-relative overflow-hidden w-100 ${loading ? 'order-flow-tracker-card-loading' : ''}`}
                    style={{ 
                      background: stage.gradient,
                      borderLeft: `4px solid ${stage.borderColor}`
                    }}
                  >
                    {/* Decorative corner element */}
                    <div 
                      className="order-flow-tracker-corner-decoration"
                      style={{ 
                        background: `linear-gradient(135deg, ${stage.borderColor}15 0%, transparent 100%)`
                      }}
                    ></div>
                    
                    <Card.Body className="d-flex flex-column px-3 pt-3 pb-0 position-relative">
                      {/* Header with Icon and Badge */}
                      <div className="d-flex justify-content-between align-items-start mb-0">
                        <div 
                          className={`order-flow-tracker-icon-container ${stage.colorClass} rounded-3 p-2 shadow-sm`}
                          style={{ background: stage.iconBg }}
                        >
                          <Icon className="order-flow-tracker-icon" size={24} strokeWidth={2.5} />
                        </div>
                        {!loading && showConversion && (
                          <Badge 
                            bg="success" 
                            className="d-flex align-items-center gap-1 px-2 py-1 order-flow-tracker-conversion-badge shadow-sm"
                            style={{ 
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              borderRadius: '1rem'
                            }}
                          >
                            <TrendingUp size={11} strokeWidth={2.5} />
                            <span>{stage.conversion}%</span>
                          </Badge>
                        )}
                      </div>
                      
                      {/* Body with Label and Value in one row */}
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        <p 
                          className="text-uppercase text-muted small fw-semibold mb-0 order-flow-tracker-label" 
                          style={{ letterSpacing: '0.1em', fontSize: '0.7rem' }}
                        >
                          {stage.label}
                        </p>
                        {loading ? (
                          <div className="d-flex align-items-center justify-content-center order-flow-tracker-value">
                            <Spinner animation="border" variant="primary" size="sm" />
                          </div>
                        ) : (
                          <h2 
                            className="fw-bold text-dark mb-0 order-flow-tracker-value" 
                            style={{ 
                              fontSize: '1.5rem',
                              lineHeight: '1.2',
                              letterSpacing: '-0.02em'
                            }}
                          >
                            {stage.value.toLocaleString()}
                          </h2>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </div>
                
                {/* Arrow between stages - Desktop */}
                {!isLast && (
                  <div className="d-flex align-items-center justify-content-center" style={{ flexShrink: 0, width: '20px' }}>
                    <div className="order-flow-tracker-arrow-wrapper position-relative">
                      <ArrowRight 
                        className="order-flow-tracker-arrow text-muted position-relative" 
                        size={18} 
                        strokeWidth={2}
                      />
                      {/* {showConversion && (
                        <div className="order-flow-tracker-arrow-badge">
                          {stage.conversion}%
                        </div>
                      )} */}
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
          
        {/* Mobile/Tablet Layout - Bootstrap Grid */}
        <Row className="d-lg-none align-items-stretch">
          {flowStages.map((stage, index) => {
            const Icon = stage.Icon;
            const isLast = index === flowStages.length - 1;
            const showConversion = stage.conversion !== undefined && stage.conversion > 0;
            
            return (
              <React.Fragment key={`mobile-${stage.label}`}>
                <Col xs={12} sm={6} className="d-flex mb-3">
                  <Card 
                    className={`order-flow-tracker-stage-card flex-fill ${stage.cardClass} border-0 shadow-sm h-100 position-relative overflow-hidden w-100 ${loading ? 'order-flow-tracker-card-loading' : ''}`}
                    style={{ 
                      background: stage.gradient,
                      borderLeft: `4px solid ${stage.borderColor}`
                    }}
                  >
                    {/* Decorative corner element */}
                    <div 
                      className="order-flow-tracker-corner-decoration"
                      style={{ 
                        background: `linear-gradient(135deg, ${stage.borderColor}15 0%, transparent 100%)`
                      }}
                    ></div>
                    
                    <Card.Body className="d-flex flex-column h-100 px-3 pt-3 pb-3 position-relative">
                      {/* Header with Icon and Badge */}
                      <div className="d-flex justify-content-between align-items-start mb-0">
                        <div 
                          className={`order-flow-tracker-icon-container ${stage.colorClass} rounded-3 p-2 shadow-sm`}
                          style={{ background: stage.iconBg }}
                        >
                          <Icon className="order-flow-tracker-icon" size={24} strokeWidth={2.5} />
                        </div>
                        {!loading && showConversion && (
                          <Badge 
                            bg="success" 
                            className="d-flex align-items-center gap-1 px-2 py-1 order-flow-tracker-conversion-badge shadow-sm"
                            style={{ 
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              borderRadius: '1rem'
                            }}
                          >
                            <TrendingUp size={11} strokeWidth={2.5} />
                            <span>{stage.conversion}%</span>
                          </Badge>
                        )}
                      </div>
                      
                      {/* Body with Label and Value in one row */}
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        <p 
                          className="text-uppercase text-muted small fw-semibold mb-0 order-flow-tracker-label" 
                          style={{ letterSpacing: '0.1em', fontSize: '0.7rem' }}
                        >
                          {stage.label}
                        </p>
                        {loading ? (
                          <div className="d-flex align-items-center justify-content-center order-flow-tracker-value">
                            <Spinner animation="border" variant="primary" size="sm" />
                          </div>
                        ) : (
                          <h2 
                            className="fw-bold text-dark mb-0 order-flow-tracker-value" 
                            style={{ 
                              fontSize: '1.5rem',
                              lineHeight: '1.2',
                              letterSpacing: '-0.02em'
                            }}
                          >
                            {stage.value.toLocaleString()}
                          </h2>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                
                {/* Mobile arrow - shown below card on small screens */}
                {!isLast && (
                  <Col xs={12} className="d-flex align-items-center justify-content-center my-2">
                    <ArrowRight 
                      className="order-flow-tracker-arrow text-muted" 
                      size={20} 
                      style={{ transform: 'rotate(90deg)' }}
                      strokeWidth={2}
                    />
                  </Col>
                )}
              </React.Fragment>
            );
          })}
        </Row>
      </Card.Body>
    </Card>
  );
}